import React from "react";
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useLocation,
  useNavigate,
  useParams,
  LinkProps,
  NavLinkProps,
  NavigateOptions,
  To,
} from "react-router-dom";
import {
  DEFAULT_LANG_PATH,
  LangPath,
  getLocalizedPath,
  isValidLangPath,
} from "../i18n/routing";

export function useCurrentLangPath(): LangPath {
  const { lang } = useParams<{ lang?: string }>();
  const location = useLocation();

  if (isValidLangPath(lang)) {
    return lang;
  }

  const firstSegment = location.pathname.split("/").filter(Boolean)[0];
  if (isValidLangPath(firstSegment)) {
    return firstSegment;
  }

  return DEFAULT_LANG_PATH;
}

export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const currentLang = useCurrentLangPath();

  return React.useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }

      if (typeof to === "string") {
        navigate(getLocalizedPath(to, currentLang), options);
      } else {
        const localizedPathname = to.pathname
          ? getLocalizedPath(to.pathname, currentLang)
          : undefined;
        navigate({ ...to, pathname: localizedPathname }, options);
      }
    },
    [navigate, currentLang]
  );
}

export interface LocalizedLinkProps extends LinkProps {
  to: To;
}

export const LocalizedLink = React.forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  ({ to, ...rest }, ref) => {
    const currentLang = useCurrentLangPath();

    let resolvedTo = to;
    if (typeof to === "string") {
      resolvedTo = getLocalizedPath(to, currentLang);
    } else if (typeof to === "object" && to.pathname) {
      resolvedTo = {
        ...to,
        pathname: getLocalizedPath(to.pathname, currentLang),
      };
    }

    return <RouterLink ref={ref} to={resolvedTo} {...rest} />;
  }
);

LocalizedLink.displayName = "LocalizedLink";

export interface LocalizedNavLinkProps extends NavLinkProps {
  to: To;
}

export const LocalizedNavLink = React.forwardRef<HTMLAnchorElement, LocalizedNavLinkProps>(
  ({ to, ...rest }, ref) => {
    const currentLang = useCurrentLangPath();

    let resolvedTo = to;
    if (typeof to === "string") {
      resolvedTo = getLocalizedPath(to, currentLang);
    } else if (typeof to === "object" && to.pathname) {
      resolvedTo = {
        ...to,
        pathname: getLocalizedPath(to.pathname, currentLang),
      };
    }

    return <RouterNavLink ref={ref} to={resolvedTo} {...rest} />;
  }
);

LocalizedNavLink.displayName = "LocalizedNavLink";

export default LocalizedLink;
